import { create } from "zustand";

/**
 * Tracks courses the user just enrolled in / purchased during this session.
 * The course detail page checks this store FIRST, before the API response.
 * This eliminates any flash of "not enrolled" after checkout.
 *
 * Entries are cleared when the API confirms the enrollment state,
 * or after 5 minutes (safety TTL).
 */

interface EnrollmentEntry {
  purchased: boolean;
  timestamp: number;
}

interface RecentEnrollmentsState {
  entries: Record<string, EnrollmentEntry>;
  markEnrolled: (courseIds: string[], purchased: boolean) => void;
  isRecentlyEnrolled: (courseId: string) => boolean;
  isRecentlyPurchased: (courseId: string) => boolean;
  clear: (courseId: string) => void;
}

const TTL_MS = 5 * 60 * 1000; // 5 minutes

export const useRecentEnrollments = create<RecentEnrollmentsState>((set, get) => ({
  entries: {},

  markEnrolled: (courseIds, purchased) =>
    set((state) => {
      const now = Date.now();
      const newEntries = { ...state.entries };
      for (const id of courseIds) {
        newEntries[id] = { purchased, timestamp: now };
      }
      return { entries: newEntries };
    }),

  isRecentlyEnrolled: (courseId) => {
    const entry = get().entries[courseId];
    if (!entry) return false;
    if (Date.now() - entry.timestamp > TTL_MS) {
      get().clear(courseId);
      return false;
    }
    return true;
  },

  isRecentlyPurchased: (courseId) => {
    const entry = get().entries[courseId];
    if (!entry) return false;
    if (Date.now() - entry.timestamp > TTL_MS) {
      get().clear(courseId);
      return false;
    }
    return entry.purchased;
  },

  clear: (courseId) =>
    set((state) => {
      const { [courseId]: _, ...rest } = state.entries;
      return { entries: rest };
    }),
}));
